/**
 * AxLabs Mathematics Experiments - Algebra
 *
 * Collection of experiments exploring algebraic structures and relationships.
 */
import { createExperimentFromSchema } from '../../framework/index.js';
import { getParam, getState } from './helpers.js';
// ============================================================================
// QUADRATIC ROOT BEHAVIOR
// ============================================================================
export const QuadraticRootBehavior = createExperimentFromSchema({
    id: 'quadratic-root-behavior',
    name: 'Quadratic Root Behavior',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Explore how coefficients affect the roots of quadratic equations',
    conceptTags: ['Quadratic Equations', 'Discriminant', 'Roots', 'Parabola'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'X Position', unit: 'dimensionless', initial: -5, symbol: 'x' },
        { id: 'y', name: 'Y Value (ax²+bx+c)', unit: 'dimensionless', initial: 16, symbol: 'y' },
        { id: 'discriminant', name: 'Discriminant', unit: 'dimensionless', initial: 1, symbol: '\\Delta' },
        { id: 'root1', name: 'Root 1', unit: 'dimensionless', initial: -1, symbol: 'x_1' },
        { id: 'root2', name: 'Root 2', unit: 'dimensionless', initial: -3, symbol: 'x_2' },
        { id: 'vertexX', name: 'Vertex X', unit: 'dimensionless', initial: -2, symbol: 'h' },
        { id: 'vertexY', name: 'Vertex Y', unit: 'dimensionless', initial: -1, symbol: 'k' },
    ],
    parameters: [
        { id: 'a', name: 'Coefficient a', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'a', step: 0.1 },
        { id: 'b', name: 'Coefficient b', unit: 'dimensionless', min: -10, max: 10, default: 4, symbol: 'b', step: 0.1 },
        { id: 'c', name: 'Coefficient c', unit: 'dimensionless', min: -10, max: 10, default: 3, symbol: 'c', step: 0.1 },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.5, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'quadratic-formula',
            name: 'Quadratic Formula',
            latex: 'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}',
            description: 'Formula for finding roots of quadratic equation',
        },
        {
            id: 'discriminant',
            name: 'Discriminant',
            latex: '\\Delta = b^2 - 4ac',
            description: 'Determines the nature of the roots',
        },
        {
            id: 'vertex-form',
            name: 'Vertex Form',
            latex: 'y = a(x-h)^2 + k, \\quad h = -\\frac{b}{2a}',
            description: 'Quadratic in vertex form with vertex at (h, k)',
        },
    ],
    derivatives: (state, params) => {
        const a = getParam(params, 'a');
        const b = getParam(params, 'b');
        const c = getParam(params, 'c');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const y = a * nextX * nextX + b * nextX + c;
        const discriminant = b * b - 4 * a * c;
        let root1 = 0;
        let root2 = 0;
        if (Math.abs(a) > 0.001) {
            if (discriminant >= 0) {
                root1 = (-b + Math.sqrt(discriminant)) / (2 * a);
                root2 = (-b - Math.sqrt(discriminant)) / (2 * a);
            }
            else {
                root1 = NaN;
                root2 = NaN;
            }
        }
        const vertexX = -b / (2 * a);
        const vertexY = a * vertexX * vertexX + b * vertexX + c;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            discriminant: discriminant - getState(state, 'discriminant'),
            root1: root1 - getState(state, 'root1'),
            root2: root2 - getState(state, 'root2'),
            vertexX: vertexX - getState(state, 'vertexX'),
            vertexY: vertexY - getState(state, 'vertexY'),
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -50, max: 50 },
    },
});
// ============================================================================
// POLYNOMIAL DEGREE EFFECTS
// ============================================================================
export const PolynomialDegreeEffects = createExperimentFromSchema({
    id: 'polynomial-degree-effects',
    name: 'Polynomial Degree Effects',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Explore how polynomial degree affects shape and behavior',
    conceptTags: ['Polynomials', 'Degree', 'End Behavior', 'Turning Points'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'X', unit: 'dimensionless', initial: -2, symbol: 'x' },
        { id: 'y', name: 'P(x)', unit: 'dimensionless', initial: -8, symbol: 'P(x)' },
        { id: 'yPrime', name: 'P\'(x)', unit: 'dimensionless', initial: 12, symbol: 'P\'(x)', description: 'Derivative' },
        { id: 'turningPoints', name: 'Max Turning Points', unit: 'dimensionless', initial: 2, symbol: 'n-1' },
        { id: 'roots', name: 'Max Real Roots', unit: 'dimensionless', initial: 3, symbol: 'n' },
    ],
    parameters: [
        { id: 'degree', name: 'Degree', unit: 'dimensionless', min: 1, max: 6, default: 3, symbol: 'n' },
        { id: 'leadingCoeff', name: 'Leading Coefficient', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'a_n' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.5, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'general-polynomial',
            name: 'General Polynomial',
            latex: 'P(x) = a_nx^n + a_{n-1}x^{n-1} + \\cdots + a_1x + a_0',
            description: 'General form of an nth-degree polynomial',
        },
        {
            id: 'fundamental-theorem',
            name: 'Fundamental Theorem of Algebra',
            latex: 'P(x) \\text{ has exactly } n \\text{ complex roots (counting multiplicity)}',
            description: 'Every degree-n polynomial has n complex roots',
        },
        {
            id: 'turning-points',
            name: 'Maximum Turning Points',
            latex: '\\text{Maximum turning points} = n - 1',
            description: 'A polynomial of degree n has at most n-1 turning points',
        },
    ],
    derivatives: (state, params) => {
        const degree = Math.round(getParam(params, 'degree'));
        const leadingCoeff = getParam(params, 'leadingCoeff');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        // Compute P(x) = leadingCoeff * x^degree + x^(degree-1) - 2x + 1
        let y = 0;
        let yPrime = 0;
        for (let i = 0; i <= degree; i++) {
            const coeff = i === degree ? leadingCoeff : (i === degree - 1 ? 1 : (i === 1 ? -2 : (i === 0 ? 1 : 0)));
            y += coeff * Math.pow(nextX, degree - i);
            if (i < degree) {
                yPrime += coeff * (degree - i) * Math.pow(nextX, degree - i - 1);
            }
        }
        const turningPoints = Math.max(0, degree - 1);
        const roots = degree;
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            yPrime: yPrime - getState(state, 'yPrime'),
            turningPoints: turningPoints - getState(state, 'turningPoints'),
            roots: roots - getState(state, 'roots'),
        };
    },
    bounds: {
        x: { min: -5, max: 5 },
        y: { min: -100, max: 100 },
    },
});
// ============================================================================
// SYSTEM OF EQUATIONS SOLUTION SPACE
// ============================================================================
export const SystemOfEquationsSolutionSpace = createExperimentFromSchema({
    id: 'system-of-equations-solution-space',
    name: 'System of Equations Solution Space',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Explore solutions to linear systems of equations',
    conceptTags: ['Linear Systems', 'Solution Space', 'Determinants', 'Matrix Rank'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Solution x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'y', name: 'Solution y', unit: 'dimensionless', initial: 1, symbol: 'y' },
        { id: 'determinant', name: 'Determinant', unit: 'dimensionless', initial: 1, symbol: '\\det' },
        { id: 'eq1Val', name: 'Equation 1 LHS', unit: 'dimensionless', initial: 5, symbol: 'E_1' },
        { id: 'eq2Val', name: 'Equation 2 LHS', unit: 'dimensionless', initial: 5, symbol: 'E_2' },
        { id: 'solutionType', name: 'Solution Type', unit: 'dimensionless', initial: 1, symbol: 'S' },
    ],
    parameters: [
        { id: 'a1', name: 'a₁ (eq1)', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'a_1' },
        { id: 'b1', name: 'b₁ (eq1)', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'b_1' },
        { id: 'c1', name: 'c₁ (eq1)', unit: 'dimensionless', min: -10, max: 10, default: 5, symbol: 'c_1' },
        { id: 'a2', name: 'a₂ (eq2)', unit: 'dimensionless', min: -5, max: 5, default: 2, symbol: 'a_2' },
        { id: 'b2', name: 'b₂ (eq2)', unit: 'dimensionless', min: -5, max: 5, default: 3, symbol: 'b_2' },
        { id: 'c2', name: 'c₂ (eq2)', unit: 'dimensionless', min: -10, max: 10, default: 7, symbol: 'c_2' },
    ],
    equations: [
        {
            id: 'cramer-rule',
            name: 'Cramer\'s Rule',
            latex: 'x = \\frac{\\det(A_x)}{\\det(A)}, \\quad y = \\frac{\\det(A_y)}{\\det(A)}',
            description: 'Solution using determinants',
        },
        {
            id: 'system-determinant',
            name: 'System Determinant',
            latex: '\\det(A) = a_1b_2 - a_2b_1',
            description: 'Determinant of coefficient matrix',
        },
    ],
    derivatives: (_state, params) => {
        const a1 = getParam(params, 'a1');
        const b1 = getParam(params, 'b1');
        const c1 = getParam(params, 'c1');
        const a2 = getParam(params, 'a2');
        const b2 = getParam(params, 'b2');
        const c2 = getParam(params, 'c2');
        const det = a1 * b2 - a2 * b1;
        let x = 0;
        let y = 0;
        let solutionType = 0; // 0: no solution, 1: unique, 2: infinite
        if (Math.abs(det) > 0.0001) {
            x = (c1 * b2 - c2 * b1) / det;
            y = (a1 * c2 - a2 * c1) / det;
            solutionType = 1;
        }
        else {
            // Parallel or same line
            const detX = c1 * b2 - c2 * b1;
            if (Math.abs(detX) < 0.0001) {
                solutionType = 2; // Infinite solutions
                x = 0;
                y = 0;
            }
            else {
                solutionType = 0; // No solution
            }
        }
        const eq1Val = a1 * x + b1 * y;
        const eq2Val = a2 * x + b2 * y;
        return {
            x: x - getState(_state, 'x'),
            y: y - getState(_state, 'y'),
            determinant: det - getState(_state, 'determinant'),
            eq1Val: eq1Val - getState(_state, 'eq1Val'),
            eq2Val: eq2Val - getState(_state, 'eq2Val'),
            solutionType: solutionType - getState(_state, 'solutionType'),
        };
    },
    bounds: {
        x: { min: -20, max: 20 },
        y: { min: -20, max: 20 },
    },
});
// ============================================================================
// INEQUALITY REGION MAPPING
// ============================================================================
export const InequalityRegionMapping = createExperimentFromSchema({
    id: 'inequality-region-mapping',
    name: 'Inequality Region Mapping',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Visualize and explore solution regions for inequalities',
    conceptTags: ['Inequalities', 'Feasible Region', 'Linear Programming', 'Graphing'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'X Coordinate', unit: 'dimensionless', initial: 0, symbol: 'x' },
        { id: 'y', name: 'Y Coordinate', unit: 'dimensionless', initial: 0, symbol: 'y' },
        { id: 'ineq1', name: 'Inequality 1 Satisfied', unit: 'dimensionless', initial: 1, symbol: 'I_1' },
        { id: 'ineq2', name: 'Inequality 2 Satisfied', unit: 'dimensionless', initial: 1, symbol: 'I_2' },
        { id: 'allSatisfied', name: 'All Satisfied', unit: 'dimensionless', initial: 1, symbol: 'A' },
        { id: 'scanAngle', name: 'Scan Angle', unit: 'rad', initial: 0, symbol: '\\theta' },
    ],
    parameters: [
        { id: 'a1', name: 'Inequality 1: a', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'a_1' },
        { id: 'b1', name: 'Inequality 1: b', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'b_1' },
        { id: 'c1', name: 'Inequality 1: c', unit: 'dimensionless', min: -10, max: 10, default: 0, symbol: 'c_1' },
        { id: 'op1', name: 'Inequality 1 Operator', unit: 'dimensionless', min: 0, max: 1, default: 1, symbol: '\\leq' },
        { id: 'a2', name: 'Inequality 2: a', unit: 'dimensionless', min: -5, max: 5, default: -1, symbol: 'a_2' },
        { id: 'b2', name: 'Inequality 2: b', unit: 'dimensionless', min: -5, max: 5, default: 1, symbol: 'b_2' },
        { id: 'c2', name: 'Inequality 2: c', unit: 'dimensionless', min: -10, max: 10, default: 4, symbol: 'c_2' },
        { id: 'scanRadius', name: 'Scan Radius', unit: 'dimensionless', min: 0.5, max: 5, default: 2, symbol: 'r' },
    ],
    equations: [
        {
            id: 'linear-inequality',
            name: 'Linear Inequality',
            latex: 'ax + by \\leq c \\quad \\text{or} \\quad ax + by \\geq c',
            description: 'General form of linear inequality',
        },
        {
            id: 'feasible-region',
            name: 'Feasible Region',
            latex: 'R = \\{(x,y) : \\text{all constraints satisfied}\\}',
            description: 'Intersection of all constraint regions',
        },
    ],
    derivatives: (state, params) => {
        const a1 = getParam(params, 'a1');
        const b1 = getParam(params, 'b1');
        const c1 = getParam(params, 'c1');
        const op1 = getParam(params, 'op1');
        const a2 = getParam(params, 'a2');
        const b2 = getParam(params, 'b2');
        const c2 = getParam(params, 'c2');
        const radius = getParam(params, 'scanRadius');
        const angle = getState(state, 'scanAngle');
        const nextAngle = angle + 0.05;
        const x = radius * Math.cos(nextAngle);
        const y = radius * Math.sin(nextAngle);
        const val1 = a1 * x + b1 * y;
        const ineq1 = (op1 > 0.5 && val1 <= c1 + 0.001) || (op1 <= 0.5 && val1 >= c1 - 0.001) ? 1 : 0;
        const val2 = a2 * x + b2 * y;
        const ineq2 = val2 <= c2 + 0.001 ? 1 : 0;
        const allSatisfied = (ineq1 > 0.5 && ineq2 > 0.5) ? 1 : 0;
        return {
            x: x - getState(state, 'x'),
            y: y - getState(state, 'y'),
            ineq1: ineq1 - getState(state, 'ineq1'),
            ineq2: ineq2 - getState(state, 'ineq2'),
            allSatisfied: allSatisfied - getState(state, 'allSatisfied'),
            scanAngle: 0.05,
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -10, max: 10 },
        scanAngle: { min: 0, max: Math.PI * 2 },
    },
});
// ============================================================================
// ABSOLUTE VALUE TRANSFORMATIONS
// ============================================================================
export const AbsoluteValueTransformations = createExperimentFromSchema({
    id: 'absolute-value-transformations',
    name: 'Absolute Value Transformations',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Explore transformations of absolute value functions',
    conceptTags: ['Absolute Value', 'Transformations', 'Piecewise Functions', 'Graphing'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 'x', name: 'X', unit: 'dimensionless', initial: -5, symbol: 'x' },
        { id: 'y', name: 'f(x)', unit: 'dimensionless', initial: 7, symbol: 'f(x)' },
        { id: 'yBase', name: 'Base |x|', unit: 'dimensionless', initial: 5, symbol: '|x|' },
        { id: 'vertexX', name: 'Vertex X', unit: 'dimensionless', initial: -2, symbol: 'h' },
        { id: 'vertexY', name: 'Vertex Y', unit: 'dimensionless', initial: 3, symbol: 'k' },
    ],
    parameters: [
        { id: 'a', name: 'Stretch Factor', unit: 'dimensionless', min: -3, max: 3, default: 1, symbol: 'a' },
        { id: 'h', name: 'Horizontal Shift', unit: 'dimensionless', min: -5, max: 5, default: 0, symbol: 'h' },
        { id: 'k', name: 'Vertical Shift', unit: 'dimensionless', min: -5, max: 5, default: 0, symbol: 'k' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.01, max: 0.2, default: 0.05, symbol: 'v' },
    ],
    equations: [
        {
            id: 'abs-value-form',
            name: 'Transformed Absolute Value',
            latex: 'f(x) = a|x-h| + k',
            description: 'General form of transformed absolute value function',
        },
        {
            id: 'vertex',
            name: 'Vertex',
            latex: '(h, k) = \\text{vertex of the V-shape}',
            description: 'The point where the V-shaped graph changes direction',
        },
    ],
    derivatives: (state, params) => {
        const a = getParam(params, 'a');
        const h = getParam(params, 'h');
        const k = getParam(params, 'k');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = x + scanRate * 0.016;
        const y = a * Math.abs(nextX - h) + k;
        const yBase = Math.abs(nextX);
        return {
            x: scanRate * 0.016,
            y: y - getState(state, 'y'),
            yBase: yBase - getState(state, 'yBase'),
            vertexX: h - getState(state, 'vertexX'),
            vertexY: k - getState(state, 'vertexY'),
        };
    },
    bounds: {
        x: { min: -10, max: 10 },
        y: { min: -20, max: 20 },
    },
});
// ============================================================================
// EXPONENTIAL VS LINEAR GROWTH
// ============================================================================
export const ExponentialVsLinearGrowth = createExperimentFromSchema({
    id: 'exponential-vs-linear-growth',
    name: 'Exponential vs Linear Growth',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Compare and contrast exponential and linear growth patterns',
    conceptTags: ['Exponential Functions', 'Linear Functions', 'Growth Rates', 'Comparing Functions'],
    difficulty: 'beginner',
    stateVariables: [
        { id: 't', name: 'Time', unit: 's', initial: 0, symbol: 't' },
        { id: 'linear', name: 'Linear: y = mt + b', unit: 'dimensionless', initial: 10, symbol: 'L(t)' },
        { id: 'exponential', name: 'Exponential: y = a·b^t', unit: 'dimensionless', initial: 5, symbol: 'E(t)' },
        { id: 'difference', name: 'E(t) - L(t)', unit: 'dimensionless', initial: -5, symbol: '\\Delta' },
        { id: 'ratio', name: 'E(t) / L(t)', unit: 'dimensionless', initial: 0.5, symbol: 'R' },
        { id: 'crossover', name: 'Crossover Occurred', unit: 'dimensionless', initial: 0, symbol: 'C' },
    ],
    parameters: [
        { id: 'm', name: 'Linear Slope', unit: 'dimensionless/s', min: 0.1, max: 5, default: 1, symbol: 'm' },
        { id: 'b', name: 'Linear Intercept', unit: 'dimensionless', min: 0, max: 20, default: 10, symbol: 'b' },
        { id: 'a', name: 'Exponential Initial', unit: 'dimensionless', min: 1, max: 10, default: 5, symbol: 'a' },
        { id: 'base', name: 'Exponential Base', unit: 'dimensionless', min: 1.01, max: 1.5, default: 1.1, symbol: 'r' },
    ],
    equations: [
        {
            id: 'linear',
            name: 'Linear Growth',
            latex: 'L(t) = mt + b',
            description: 'Grows by constant amount each time period',
        },
        {
            id: 'exponential',
            name: 'Exponential Growth',
            latex: 'E(t) = a \\cdot r^t',
            description: 'Grows by constant percentage each time period',
        },
        {
            id: 'crossover',
            name: 'Crossover Point',
            latex: 'a \\cdot r^t = mt + b',
            description: 'Time when exponential equals linear',
        },
    ],
    derivatives: (state, params) => {
        const m = getParam(params, 'm');
        const b = getParam(params, 'b');
        const a = getParam(params, 'a');
        const r = getParam(params, 'base');
        const t = getState(state, 't');
        const nextT = t + 0.016;
        const linear = m * nextT + b;
        const exponential = a * Math.pow(r, nextT);
        const difference = exponential - linear;
        const ratio = linear > 0.001 ? exponential / linear : 0;
        const prevDiff = getState(state, 'difference');
        const crossover = (prevDiff < 0 && difference >= 0) ? 1 : 0;
        return {
            t: 0.016,
            linear: linear - getState(state, 'linear'),
            exponential: exponential - getState(state, 'exponential'),
            difference: difference - prevDiff,
            ratio: ratio - getState(state, 'ratio'),
            crossover: crossover - getState(state, 'crossover'),
        };
    },
    bounds: {
        t: { min: 0, max: 120 },
        linear: { min: 0, max: 1000 },
        exponential: { min: 0, max: 10000 },
    },
});
// ============================================================================
// LOGARITHMIC SCALING BEHAVIOR
// ============================================================================
export const LogarithmicScalingBehavior = createExperimentFromSchema({
    id: 'logarithmic-scaling-behavior',
    name: 'Logarithmic Scaling Behavior',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Explore how logarithmic functions compress and scale data',
    conceptTags: ['Logarithms', 'Scaling', 'Data Compression', 'Inverse of Exponential'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'x', name: 'Input x', unit: 'dimensionless', initial: 1, symbol: 'x' },
        { id: 'log10', name: 'log₁₀(x)', unit: 'dimensionless', initial: 0, symbol: '\\log_{10}(x)' },
        { id: 'ln', name: 'ln(x)', unit: 'dimensionless', initial: 0, symbol: '\\ln(x)' },
        { id: 'log2', name: 'log₂(x)', unit: 'dimensionless', initial: 0, symbol: '\\log_2(x)' },
        { id: 'ratio', name: 'ln(x)/log₁₀(x)', unit: 'dimensionless', initial: 2.3, symbol: '\\lambda' },
    ],
    parameters: [
        { id: 'maxX', name: 'Maximum X', unit: 'dimensionless', min: 10, max: 1000, default: 100, symbol: 'x_{max}' },
        { id: 'scanRate', name: 'Scan Rate', unit: 'dimensionless/s', min: 0.1, max: 2, default: 0.5, symbol: 'v' },
    ],
    equations: [
        {
            id: 'log-change-base',
            name: 'Change of Base Formula',
            latex: '\\log_a(x) = \\frac{\\ln(x)}{\\ln(a)}',
            description: 'Convert between different logarithm bases',
        },
        {
            id: 'log-properties',
            name: 'Logarithm Properties',
            latex: '\\log(ab) = \\log(a) + \\log(b), \\quad \\log(a^n) = n\\log(a)',
            description: 'Key properties of logarithms',
        },
    ],
    derivatives: (state, params) => {
        const maxX = getParam(params, 'maxX');
        const scanRate = getParam(params, 'scanRate');
        const x = getState(state, 'x');
        const nextX = Math.min(x + scanRate * 0.016, maxX);
        const log10 = Math.log10(Math.max(0.001, nextX));
        const ln = Math.log(Math.max(0.001, nextX));
        const log2 = Math.log2(Math.max(0.001, nextX));
        const ratio = Math.abs(log10) > 0.0001 ? ln / log10 : 0;
        return {
            x: nextX - x,
            log10: log10 - getState(state, 'log10'),
            ln: ln - getState(state, 'ln'),
            log2: log2 - getState(state, 'log2'),
            ratio: ratio - getState(state, 'ratio'),
        };
    },
    bounds: {
        x: { min: 0.001, max: 1000 },
        log10: { min: -3, max: 3 },
        ln: { min: -7, max: 7 },
    },
});
// ============================================================================
// COMPLEX NUMBER PLANE MAPPING
// ============================================================================
export const ComplexNumberPlaneMapping = createExperimentFromSchema({
    id: 'complex-number-plane-mapping',
    name: 'Complex Number Plane Mapping',
    domain: 'mathematics',
    category: 'algebra',
    description: 'Visualize operations on the complex number plane',
    conceptTags: ['Complex Numbers', 'Argand Diagram', 'Polar Form', 'Modulus', 'Argument'],
    difficulty: 'intermediate',
    stateVariables: [
        { id: 'zReal', name: 'z Real Part', unit: 'dimensionless', initial: 3, symbol: '\\Re(z)' },
        { id: 'zImag', name: 'z Imaginary Part', unit: 'dimensionless', initial: 4, symbol: '\\Im(z)' },
        { id: 'modulus', name: '|z| Modulus', unit: 'dimensionless', initial: 5, symbol: '|z|' },
        { id: 'argument', name: 'arg(z) Argument', unit: 'rad', initial: 0.93, symbol: '\\arg(z)' },
        { id: 'zSquaredReal', name: 'z² Real', unit: 'dimensionless', initial: -7, symbol: '\\Re(z^2)' },
        { id: 'zSquaredImag', name: 'z² Imag', unit: 'dimensionless', initial: 24, symbol: '\\Im(z^2)' },
    ],
    parameters: [
        { id: 'r', name: 'Radius', unit: 'dimensionless', min: 0.5, max: 5, default: 3, symbol: 'r' },
        { id: 'thetaRate', name: 'Angular Rate', unit: 'rad/s', min: -2, max: 2, default: 0.5, symbol: '\\omega' },
        { id: 'operation', name: 'Operation', unit: 'dimensionless', min: 0, max: 3, default: 1, symbol: 'op' },
    ],
    equations: [
        {
            id: 'complex-polar',
            name: 'Polar Form',
            latex: 'z = r(\\cos\\theta + i\\sin\\theta) = re^{i\\theta}',
            description: 'Complex number in polar (exponential) form',
        },
        {
            id: 'modulus',
            name: 'Modulus',
            latex: '|z| = \\sqrt{\\Re(z)^2 + \\Im(z)^2}',
            description: 'Distance from origin in complex plane',
        },
        {
            id: 'argument',
            name: 'Argument',
            latex: '\\arg(z) = \\arctan\\left(\\frac{\\Im(z)}{\\Re(z)}\\right)',
            description: 'Angle with positive real axis',
        },
        {
            id: 'de-moivre',
            name: 'De Moivre\'s Theorem',
            latex: 'z^n = r^n(\\cos n\\theta + i\\sin n\\theta)',
            description: 'Formula for powers of complex numbers',
        },
    ],
    derivatives: (state, params) => {
        const r = getParam(params, 'r');
        const thetaRate = getParam(params, 'thetaRate');
        const operation = Math.round(getParam(params, 'operation'));
        const currentModulus = getState(state, 'modulus');
        const currentArg = getState(state, 'argument');
        const nextArg = currentArg + thetaRate * 0.016;
        let zReal = r * Math.cos(nextArg);
        let zImag = r * Math.sin(nextArg);
        let zSquaredReal = 0;
        let zSquaredImag = 0;
        switch (operation) {
            case 0: // Identity
                zSquaredReal = zReal;
                zSquaredImag = zImag;
                break;
            case 1: // Square
                zSquaredReal = zReal * zReal - zImag * zImag;
                zSquaredImag = 2 * zReal * zImag;
                break;
            case 2: // Cube
                const z2Real = zReal * zReal - zImag * zImag;
                const z2Imag = 2 * zReal * zImag;
                zSquaredReal = zReal * z2Real - zImag * z2Imag;
                zSquaredImag = zReal * z2Imag + zImag * z2Real;
                break;
            case 3: // Conjugate
                zSquaredReal = zReal;
                zSquaredImag = -zImag;
                break;
        }
        const modulus = Math.sqrt(zReal * zReal + zImag * zImag);
        const argument = Math.atan2(zImag, zReal);
        return {
            zReal: zReal - getState(state, 'zReal'),
            zImag: zImag - getState(state, 'zImag'),
            modulus: modulus - currentModulus,
            argument: argument - currentArg,
            zSquaredReal: zSquaredReal - getState(state, 'zSquaredReal'),
            zSquaredImag: zSquaredImag - getState(state, 'zSquaredImag'),
        };
    },
    bounds: {
        zReal: { min: -10, max: 10 },
        zImag: { min: -10, max: 10 },
        modulus: { min: 0, max: 15 },
        argument: { min: -Math.PI, max: Math.PI },
    },
});
// ============================================================================
// EXPORT ALL EXPERIMENTS
// ============================================================================
export const ALL_ALGEBRA = [
    QuadraticRootBehavior,
    PolynomialDegreeEffects,
    SystemOfEquationsSolutionSpace,
    InequalityRegionMapping,
    AbsoluteValueTransformations,
    ExponentialVsLinearGrowth,
    LogarithmicScalingBehavior,
    ComplexNumberPlaneMapping,
];
//# sourceMappingURL=algebra.js.map